import { useEffect, useRef } from 'react';
import type { IconType } from 'react-icons';
import { IoClose, IoDocumentTextOutline } from 'react-icons/io5';

import { ButtonSmall } from '@/components/Button';
import { Input } from '@/components/Inputs';

import type { NarrativeEntry } from '../types';

import s from './EntryListSection.module.scss';
import { SectionHeader } from './SectionHeader';

interface EntryListSectionProps {
  icon?: IconType;
  title: string;
  addLabel: string;
  entries: NarrativeEntry[];
  onAdd: () => void;
  onUpdate: (id: string, patch: Partial<NarrativeEntry>) => void;
  onRemove: (id: string) => void;
}

const resizeTextarea = (el: HTMLTextAreaElement) => {
  el.style.height = 'auto';
  el.style.height = Math.max(60, el.scrollHeight) + 'px';
};

export function EntryListSection({
  icon,
  title,
  addLabel,
  entries,
  onAdd,
  onUpdate,
  onRemove,
}: EntryListSectionProps) {
  const textareaRefs = useRef<Record<string, HTMLTextAreaElement>>({});

  useEffect(() => {
    entries.forEach((entry) => {
      const textarea = textareaRefs.current[entry.id];
      if (textarea) resizeTextarea(textarea);
    });
  }, [entries]);

  return (
    <div className={s.section}>
      <SectionHeader icon={icon || IoDocumentTextOutline} title={title} />

      <div className={s.tableContainer}>
        <div className={s.headerRow}>
          <div className={s.colEntry}>Entry</div>
          <div className={s.colInitials}>Initials</div>
          <div className={s.colAction}></div>
        </div>

        {entries.map((entry) => (
          <div key={entry.id} className={s.row}>
            <Input
              value={entry.text}
              onChange={(e) => {
                onUpdate(entry.id, { text: e.target.value });
              }}
              placeholder="Fire drill held, all crew present."
            />
            <Input
              type="text"
              value={entry.initials}
              onChange={(e) => onUpdate(entry.id, { initials: e.target.value.slice(0, 5) })}
              maxLength={5}
              placeholder="JM"
            />
            <button className={s.removeBtn} onClick={() => onRemove(entry.id)} title="Remove Entry">
              <IoClose />
            </button>
          </div>
        ))}
      </div>

      <ButtonSmall onClick={onAdd}>+ {addLabel}</ButtonSmall>
    </div>
  );
}
